import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import withReactContent from "sweetalert2-react-content";
import { RegisterProducerCenter } from "../core/_requests";
import Swal from "sweetalert2";
import { GetProducerCenterByName } from "../core/_requests";
import { UserProducerCenter } from "../../auth";
import { useAuth } from "../../auth";
import { handleApiError } from "../../utils/apiUtils";
import { useFirstLogin } from "../../auth/core/TourGuideContext";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const RegisterProducerCenterStep2 = ({
  hideModal,
  loadStep2,
  nextStepLoadFailed,
  showStep1,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryReferrerCode = searchParams.get("referrerCode") ?? "";
  const hasValidQueryReferrerCode = /^\d{6}$/.test(queryReferrerCode);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const acceptableFormatForIcon = process.env.REACT_APP_ACCEPTABLE_ICON;
  const MAX_ICON_LENGTH = process.env.REACT_APP_MAX_ICON_LENGTH;
  const [imageSizeError, setImageSizeError] = useState(false);
  const [imageFile, setImageFile] = useState();
  const [producerCenterName, setProducerCenterName] = useState("");
  const [isProducerCenterNameExist, setIsProducerCenterNameExist] = useState<boolean>();
  const [producerCenterCode, setProducerCenterCode] = useState("");
  const [showProducerCenterCode, setShowProducerCenterCode] = useState(false);
  const { saveUserProducerCenter } = useAuth();
  const { setFirstLoginStatus } = useFirstLogin();

  const initialValues = {
    name: "",
    icon: null,
    referrerCode: hasValidQueryReferrerCode ? queryReferrerCode : "",
  };

  const [image, setImage] = useState<string | null>(null);
  const handleUploadImage = async (event) => {
    if (typeof MAX_ICON_LENGTH !== "undefined" && event.target.files[0]?.size > MAX_ICON_LENGTH)
      setImageSizeError(true);
    else setImageSizeError(false);
    const selectedFile = event.target.files[0];
    setImageFile(selectedFile);
    await formik.setFieldValue("icon", event.currentTarget.files[0]);
    formik.setFieldTouched("icon", true);

    ///////////////////
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          setImage(result);
        }
      };
      reader.readAsDataURL(file);
    } else {
      setImage("");
    }
  };

  useEffect(() => {
    if (loadStep2 == true) {
      nextStepLoadFailed();
    }
  }, [loadStep2]);

  const handleNameChange = async (event) => {
    let value = event.target.value;
    if (value !== "" && value !== producerCenterName) {
      setProducerCenterName(event.target.value);
      let getProducerCenterByNameResult = await GetProducerCenterByName(value);
      if (getProducerCenterByNameResult.data?.isSuccess == true) {
        if (getProducerCenterByNameResult.data.data.isExist == true) {
          setIsProducerCenterNameExist(true);
        } else {
          setIsProducerCenterNameExist(false);
        }
      }
    }
  };

  useEffect(() => {
    let name = formik.getFieldProps("name");
    if (name.value != "") {
      formik.setFieldTouched("name", true);
    }
  }, [isProducerCenterNameExist]);

  const registrationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t("PRODUCER.CENTER_NAME_REQUIRED"))
      .min(3, t("PRODUCER.CENTER_NAME_MIN"))
      .max(300, t("PRODUCER.CENTER_NAME_MAX"))
      .test("unique", t("PRODUCER.NAME_EXISTS"), (value) => {
        if (isProducerCenterNameExist == true) return false;
        else return true;
      }),
    referrerCode: Yup.string()
      .matches(/^\d{6}$/, {
        message: t("PRODUCER.REFERRER_CODE_INVALID"),
        excludeEmptyString: true,
      }),
    icon: Yup.string()
      .nullable()
      .test(
        "maxSize",
        t("PRODUCER.ICON_MAX_SIZE", { size: MAX_ICON_LENGTH }),
        (value) => {
          if (imageSizeError === true) return false;
          else return true;
        }
      ),
  });

  const showSwal = () => {
    withReactContent(Swal)
      .fire({
        title: <i>{t("COMMON.SUCCESS_OPERATION")}</i>,
        html: (
          <div className="center-invite-div">
            <p className="center-invite-title">
              {t("PRODUCER.CREATED_CODE", { code: producerCenterCode })}
            </p>
            <p className="center-invite-p">
              {t("PRODUCER.DOCS_INTRO")}
            </p>
            <p>
              <a
                href="https://docs.airngin.com"
                target="_blank"
                className="center-invite-link"
              >
                {t("PRODUCER.VIEW_DOCS")}
              </a>
            </p>
            <p className="center-invite-p">
              {t("PRODUCER.ADD_DEVICE_GUIDE_INTRO")}
            </p>
            <p>
              <a
                href="https://airngin.com/road-map"
                target="_blank"
                className="center-invite-link"
              >
                {t("PRODUCER.VIEW_ADD_DEVICE_GUIDE")}
              </a>
            </p>
            <p className="center-invite-p">
              {t("PRODUCER.ESP32_LIBRARY_INTRO")}
            </p>
            <p>
              <a
                href="https://github.com/AirNgin/Airngin-esp32-mqtt-client"
                target="_blank"
                className="center-invite-link"
              >
                {t("PRODUCER.VIEW_ARDUINO_LIBRARY")}
              </a>
            </p>
            <p className="center-invite-p">
              {t("PRODUCER.QUICK_START_QUESTION")}

              <a
                className="link center-invite-link"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("/devices/list");
                }}
              >
                {t("PRODUCER.START_NOW")}
              </a>
            </p>
          </div>
        ),
        icon: "success",
        showConfirmButton: true,
        confirmButtonText: t("AUTH.REGISTER_LOGIN.OK"),
        allowOutsideClick: false,
        backdrop: true,
        allowEscapeKey: false,
        width: "40%"
      })
      .then((result) => {
        if (result.isConfirmed) {
          setFirstLoginStatus(true);
        }
      });
  };

  useEffect(() => {
    if (showProducerCenterCode == true) {
      showSwal();
      setShowProducerCenterCode(true);
    }
  }, [showProducerCenterCode]);

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        setLoading(true);
        const registerProducerCenterResult = await RegisterProducerCenter(
          values.name,
          imageFile,
          hasValidQueryReferrerCode ? queryReferrerCode : values.referrerCode || undefined
        );
        if (registerProducerCenterResult.data?.isSuccess == true) {
          setProducerCenterCode(registerProducerCenterResult.data.data.code);
          setShowProducerCenterCode(true);
          setWarningMessage("");
          setShowWarningMessage(false);
          setErrorMessage("");
          setShowErrorMessage(false);
          setSubmitting(true);
          setLoading(false);
          hideModal();
          formik.resetForm({ values: formik.initialValues });

          const userproducerCenter: UserProducerCenter = {
            producerCenterId: registerProducerCenterResult.data.data.producerCenterId,
            name: registerProducerCenterResult.data.data.name,
            code: registerProducerCenterResult.data.data.code,
            icon: registerProducerCenterResult.data.data.icon,
            isMaster: registerProducerCenterResult.data.data.isMaster,
          };
          saveUserProducerCenter(userproducerCenter);
        } else if (registerProducerCenterResult.data?.isSuccess == false) {
          handleApiError(
            registerProducerCenterResult,
            setWarningMessage,
            setShowWarningMessage,
            setErrorMessage,
            setShowErrorMessage
          );
          setSubmitting(false);
          setLoading(false);
        } else {
          if (registerProducerCenterResult?.response?.data == "Access Denied !") {
            setErrorMessage(t("COMMON.ACCESS_DENIED"));
            setShowErrorMessage(true);
          } else {
            setErrorMessage(`${t("PRODUCER.REGISTER_ERROR")} : \n ${registerProducerCenterResult}`);
            setShowErrorMessage(true);
          }

          setSubmitting(false);
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        setStatus(t("AUTH.VERIFY.UNKNOWN_ERROR"));
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <div
      className={showStep1 === false ? "current" : undefined}
      data-kt-stepper-element="content"
    >
      <form
        className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        noValidate
        id="kt_add_operation_form"
        onSubmit={formik.handleSubmit}
      >
        {showErrorMessage ? (
          <div className="alert alert-danger d-flex align-items-center p-5 mb-10">
            <span className="svg-icon svg-icon-2hx svg-icon-danger me-3"></span>
            <div className="d-flex flex-column">
              <span>{errorMessage}</span>
            </div>
          </div>
        ) : null}
        {showWarningMessage ? (
          <div className="alert alert-warning d-flex align-items-center p-5 mb-10">
            <span className="svg-icon svg-icon-2hx svg-icon-warning me-3"></span>
            <div className="d-flex flex-column">
              <span>{warningMessage}</span>
            </div>
          </div>
        ) : null}

        <div className="mh-400px scroll-y me-n7 pe-7">
          <div className="mb-10">
            <label className="form-label">{t("PRODUCER.YOUR_NAME")}</label>
            <input
              type="text"
              autoComplete="off"
              placeholder={t("PRODUCER.YOUR_NAME")}
              {...formik.getFieldProps("name")}
              onBlur={handleNameChange}
              className={clsx(
                "form-control bg-transparent",
                {
                  "is-invalid": formik.touched.name && formik.errors.name,
                },
                {
                  "is-valid": formik.touched.name && !formik.errors.name,
                }
              )}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.name}</div>
              </div>
            )}
          </div>
          <div className="mb-10">
            <label className="form-label">{t("PRODUCER.REFERRER_CODE")}</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="off"
              placeholder={t("PRODUCER.REFERRER_CODE")}
              {...formik.getFieldProps("referrerCode")}
              value={hasValidQueryReferrerCode ? queryReferrerCode : formik.values.referrerCode}
              readOnly={hasValidQueryReferrerCode}
              onChange={(event) => {
                if (!hasValidQueryReferrerCode) {
                  formik.setFieldValue("referrerCode", event.target.value.replace(/\D/g, "").slice(0, 6));
                }
              }}
              className={clsx(
                "form-control bg-transparent",
                {
                  "is-invalid": formik.touched.referrerCode && formik.errors.referrerCode,
                },
                {
                  "is-valid": formik.touched.referrerCode && !formik.errors.referrerCode,
                }
              )}
            />
            {formik.touched.referrerCode && formik.errors.referrerCode && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.referrerCode}</div>
              </div>
            )}
            <div className="form-text">
              {hasValidQueryReferrerCode
                ? t("PRODUCER.REFERRER_CODE_APPLIED")
                : t("PRODUCER.REFERRER_CODE_HELP")}
            </div>
          </div>
          <div className="mb-10">
            <label className="form-label">{t("PRODUCER.YOUR_LOGO")}</label>
            <input
              type="file"
              id="icon"
              name="icon"
              onChange={handleUploadImage}
              accept={acceptableFormatForIcon}
              className={clsx(
                "form-control bg-transparent",
                {
                  "is-invalid": formik.touched.icon && formik.errors.icon,
                },
                {
                  "is-valid": formik.touched.icon && !formik.errors.icon,
                }
              )}
            />
            {formik.touched.icon && formik.errors.icon && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.icon}</div>
              </div>
            )}
            {image && (
              <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                <img src={`${image}`} alt="Metornic" />
              </div>
            )}
          </div>
        </div>
        <button
          type={!loading ? "submit" : "button"}
          className="btn btn-lg btn-main w-100 mb-5"
        >
          {!loading && <span className="indicator-label">{t("COMMON.SUBMIT")}</span>}
          {loading && (
            <span className="indicator-progress display-block">
              {t("AUTH.PLEASE_WAIT")}{" "}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </form>
    </div>
  );
};

export { RegisterProducerCenterStep2 };
